import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, TrendingUp, Award, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { mockData } from '../mock';
import * as Icons from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - handles both relative and absolute paths
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const Home = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [programs, setPrograms] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [programsRes, newsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/programs?published_only=true`),
        axios.get(`${BACKEND_URL}/api/news?published_only=true`)
      ]);
      setPrograms(programsRes.data);
      setNewsArticles(newsRes.data.slice(0, 3));
    } catch (error) {
      // Fallback to mock data
      setPrograms(mockData.programs);
      setNewsArticles(mockData.newsArticles);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % mockData.heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate testimonials every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % mockData.testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const getIcon = (iconName) => {
    const iconMap = {
      'utensils': Icons.Utensils,
      'home': Icons.Home,
      'book-open': Icons.BookOpen,
      'briefcase': Icons.Briefcase,
      'heart': Icons.Heart,
      'hand-helping': Icons.HandHeart,
      'church': Icons.Church,
      'users': Icons.Users,
      'heart-pulse': Icons.HeartPulse,
      'trending-up': Icons.TrendingUp
    };
    const IconComponent = iconMap[iconName] || Icons.Circle;
    return <IconComponent className="w-6 h-6" />;
  };

  const displayPrograms = programs.length > 0 ? programs : mockData.programs;
  const displayNews = newsArticles.length > 0 ? newsArticles : mockData.newsArticles;

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Hero Background Image */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A345E]/80 via-[#0A345E]/70 to-[#007A7A]/60 z-10"></div>
          {mockData.heroImages.map((image, index) => (
            <div
              key={`hero-${image.slice(-20)}`}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                currentHeroImage === index ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          ))}
        </div>

        <div className="container mx-auto max-w-6xl relative z-20">
          <div className="text-center space-y-8">
            <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium">
              501(c)(3) Nonprofit Organization
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight drop-shadow-lg">
              Come On In<br />
              <span className="text-3xl md:text-4xl lg:text-5xl">The Door Is Open To A Brighter Future</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/95 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
              {mockData.organization.mission}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link to="/donate">
                <Button className="bg-white text-[#0A345E] hover:bg-white/90 px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300" data-testid="hero-donate-btn">
                  <Heart className="w-5 h-5 mr-2" />
                  Make a Donation
                </Button>
              </Link>
              <Link to="/volunteer">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0A345E] px-8 py-6 text-lg transition-all duration-300" data-testid="hero-volunteer-btn">
                  <Users className="w-5 h-5 mr-2" />
                  Get Involved
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#F5F2E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockData.stats.map((stat, index) => (
              <div
                key={`stat-${stat.label}`}
                className="text-center p-6 rounded-xl bg-gradient-to-br from-[#E8E2D0] to-white hover:shadow-lg transition-shadow duration-300"
              >
                <div className="text-4xl md:text-5xl font-bold text-[#007A7A] mb-2">{stat.number}</div>
                <div className="text-sm md:text-base text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A345E] mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive services designed to empower and uplift our community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="programs-grid">
            {displayPrograms.slice(0, 4).map((program) => (
              <Card
                key={program.id}
                onClick={() => setSelectedProgram(program)}
                className="hover:shadow-xl transition-all duration-300 border-0 overflow-hidden group cursor-pointer"
                data-testid={`program-card-${program.id}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={resolveImageUrl(program.image)} 
                    alt={program.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A345E]/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-[#007A7A]">{getIcon(program.icon)}</div>
                    </div>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl text-[#0A345E]">{program.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600">{program.description}</CardDescription>
                  <div className="mt-4 text-[#007A7A] font-medium text-sm flex items-center">
                    Learn More <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/programs">
              <Button className="bg-[#007A7A] hover:bg-[#007A7A]/90 text-white px-8 py-6 text-lg" data-testid="view-all-programs-btn">
                View All Programs
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Program Detail Modal */}
      {selectedProgram && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProgram(null)}
          data-testid="program-modal"
        >
          <Card 
            className="max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64 overflow-hidden">
              <img 
                src={resolveImageUrl(selectedProgram.image)} 
                alt={selectedProgram.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A345E]/90 to-transparent"></div>
              <button
                onClick={() => setSelectedProgram(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors duration-200"
                data-testid="close-modal-btn"
              >
                <Icons.X className="w-6 h-6 text-gray-700" />
              </button>
              <div className="absolute bottom-6 left-6">
                <div className="w-16 h-16 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center mb-4">
                  <div className="text-[#007A7A]">{getIcon(selectedProgram.icon)}</div>
                </div>
                <h2 className="text-3xl font-bold text-white">{selectedProgram.title}</h2>
              </div>
            </div>
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">{selectedProgram.description}</p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">{selectedProgram.details}</p>
              <div className="flex gap-4">
                <Link to="/donate" className="flex-1">
                  <Button className="w-full bg-[#007A7A] hover:bg-[#007A7A]/90 text-white">
                    Support This Program
                  </Button>
                </Link>
                <Link to="/volunteer" className="flex-1">
                  <Button variant="outline" className="w-full border-[#007A7A] text-[#007A7A] hover:bg-[#007A7A] hover:text-white">
                    Volunteer
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Testimonials Section */}
      <section className="py-20 bg-[#F5F2E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A345E] mb-4">Stories of Impact</h2>
            <p className="text-xl text-gray-600">The hope of a life changed through faith</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-2xl bg-white">
              <CardContent className="p-8 md:p-12">
                <div className="text-6xl text-[#007A7A] mb-6">&ldquo;</div>
                <p className="text-xl md:text-2xl text-gray-700 mb-8 italic leading-relaxed">
                  {mockData.testimonials[activeTestimonial].quote}
                </p>
                <div className="mb-6">
                  <p className="text-lg font-semibold text-[#0A345E]">
                    {mockData.testimonials[activeTestimonial].name}
                  </p>
                  <p className="text-sm text-gray-600">{mockData.testimonials[activeTestimonial].role}</p>
                </div>
                <div className="bg-[#007A7A]/10 border-l-4 border-[#007A7A] p-4 rounded">
                  <p className="text-sm text-[#0A345E] italic">
                    {mockData.testimonials[activeTestimonial].verse}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial Navigation */}
            <div className="flex justify-center space-x-3 mt-8">
              {mockData.testimonials.map((testimonial, index) => (
                <button
                  key={`testimonial-nav-${testimonial.name}`}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    activeTestimonial === index ? 'bg-[#007A7A] w-8' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img 
            src="https://images.unsplash.com/photo-1527525443983-6e60c75fff46?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Community"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A345E]/90 to-[#007A7A]/85"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">Be the Change</h2>
          <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-white/95">
            Your support makes a real difference in the lives of children, families, and veterans in our community.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/donate">
              <Button className="bg-white text-[#0A345E] hover:bg-white/90 px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                <Heart className="w-5 h-5 mr-2" />
                Donate Now
              </Button>
            </Link>
            <Link to="/volunteer">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A345E] px-8 py-6 text-lg transition-all duration-300"
              >
                <Users className="w-5 h-5 mr-2" />
                Volunteer Today
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Latest News</h2>
            <p className="text-xl text-gray-600">Stay updated with our community impact</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="news-grid">
            {displayNews.map((article) => (
              <Link to={`/news/${article.id}`} key={article.id} className="block">
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-0 group cursor-pointer" data-testid={`news-card-${article.id}`}>
                  <CardHeader>
                    <div className="text-sm text-blue-600 font-medium mb-2">{article.category}</div>
                    <CardTitle className="text-xl group-hover:text-blue-600 transition-colors duration-200">
                      {article.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-500">
                      {article.date || (article.published_date && new Date(article.published_date).toLocaleDateString())}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{article.excerpt}</p>
                    <div className="mt-4 flex items-center text-blue-600 font-medium text-sm group-hover:translate-x-2 transition-transform duration-200">
                      Read More <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/news">
              <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg" data-testid="view-all-news-btn">
                View All News
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
